import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ClipboardList, Download, AlertTriangle } from 'lucide-react';
import { usePermissions } from '@contexts/AuthContext';
import { assessmentService } from '@services';
import { usePagination } from '@hooks/usePagination';
import { useColumnFilters } from '@hooks/useColumnFilters';
import { useTableSort } from '@hooks/useTableSort';
import { useAssessmentPageData } from '@hooks/useAssessmentPageData';
import { useScheduleConflictFilter } from '@hooks/useScheduleConflictFilter';
import PageLayout from '@components/layout/PageLayout';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import TierBadge from '@components/common/TierBadge';
import Tooltip from '@components/common/Tooltip';
import ListPageHeader from '@components/common/ListPageHeader';
import { FilterButtons } from '@components/common/FilterButtons';
import { RZTable, ColumnSelector } from '@components/common/table';
import { Assessment, TableColumn } from '@app-types';
import { exportAssessments, formatAssessmentId } from '@utils/exportUtils';
import { hasOpenConflicts, getTierConflictTooltip } from '@utils/conflictUtils';
import { tierToVariant } from '@utils/badgeMappers';
import { statusToVariant } from '@utils/badgeMappers';
import { formatDateOnly, formatMediumCalendarDate } from '@utils/dateUtils';
import { formatCurrency } from '@utils/currencyUtils';
import { getGapsIdentifiedCount } from '@utils/assessmentUtils';
import { getApiErrorMessage } from '@utils/apiError';
import { showToast } from '@utils/toast';
import { LoadingOverlay } from '@components/common/loading';
import styles from './AssessmentsPage.module.css';

// Base status filter options - conflicts button is added dynamically
const baseStatusFilters = [
  { key: 'all', label: 'All' },
  { key: 'Pending', label: 'Pending' },
  { key: 'Pending Evidence', label: 'Pending Evidence' },
  { key: 'Evidence Past Due', label: 'Evidence Past Due' },
  { key: 'Escalated', label: 'Escalated' },
  { key: 'Final', label: 'Final' },
  { key: 'Cancelled', label: 'Cancelled' },
  { key: 'Expired', label: 'Expired' }
];

// Column configuration for table
const columns: TableColumn[] = [
  {
    key: 'assessment_id',
    label: 'Assessment #',
    sortable: true,
    width: '120px',
    defaultVisible: true,
    alwaysVisible: true,
    filterKey: 'assessment_id'
  },
  {
    key: 'name',
    label: 'Assessment Name',
    sortable: true,
    width: '200px',
    defaultVisible: true,
    filterKey: 'assessment_name'
  },
  {
    key: 'supplier',
    label: 'Supplier',
    sortable: true,
    width: '180px',
    defaultVisible: true,
    filterKey: 'supplier_name'
  },
  { key: 'tier', label: 'Tier', sortable: true, width: '150px', defaultVisible: true, filterKey: 'tier_name' },
  {
    key: 'contract',
    label: 'Contract Number',
    sortable: true,
    width: '140px',
    defaultVisible: true,
    filterKey: 'contract_number'
  },
  { key: 'contract_name', label: 'Contract Name', sortable: false, defaultVisible: false, filterable: false },
  { key: 'contract_start_date', label: 'Contract Start Date', sortable: false, defaultVisible: false, filterable: false },
  { key: 'contract_end_date', label: 'Contract End Date', sortable: false, defaultVisible: false, filterable: false },
  { key: 'contract_amount', label: 'Contract Amount', sortable: false, defaultVisible: false, filterable: false },
  { key: 'contract_type', label: 'Contract Type', sortable: false, defaultVisible: false, filterable: false },
  { key: 'cost_center', label: 'Cost Center', sortable: false, defaultVisible: false, filterable: false },
  { key: 'division', label: 'Division', sortable: false, defaultVisible: false, filterable: false },
  { key: 'contract_owner', label: 'Contract Owner', sortable: false, defaultVisible: false, filterable: false },
  { key: 'contract_manager', label: 'Contract Manager', sortable: false, defaultVisible: false, filterable: false },
  { key: 'assessment_year', label: 'Assessment Year', sortable: false, defaultVisible: false, filterable: false },
  {
    key: 'assessment_meeting_date',
    label: 'Assessment Meeting Date',
    sortable: false,
    defaultVisible: false,
    filterable: false
  },
  {
    key: 'status',
    label: 'Status',
    sortable: true,
    width: '120px',
    defaultVisible: true,
    filterKey: 'assessment_status'
  },
  {
    key: 'assessor',
    label: 'Assessor Name',
    sortable: true,
    width: '160px',
    defaultVisible: true,
    filterKey: 'assessor_name'
  },
  {
    key: 'assessment_report_date',
    label: 'Assessment Report Date',
    sortable: false,
    defaultVisible: false,
    filterable: false
  },
  { key: 'evidence_due_date', label: 'Evidence Due Date', sortable: false, defaultVisible: false, filterable: false },
  {
    key: 'date_evidence_verified',
    label: 'Date Evidence Verified',
    sortable: false,
    defaultVisible: false,
    filterable: false
  },
  { key: 'gaps_identified', label: 'Gaps Identified', sortable: false, defaultVisible: false, filterable: false },
  { key: 'gaps_remediated', label: 'Gaps Remediated', sortable: false, defaultVisible: false, filterable: false },
  {
    key: 'gaps_not_remediated',
    label: 'Gaps Not Remediated',
    sortable: false,
    defaultVisible: false,
    filterable: false
  },
  { key: 'created_at', label: 'Created', sortable: true, width: '110px', defaultVisible: true, filterable: false }
];

// Backend field mapping
const fieldMapping: Record<string, string> = {
  assessment_id: 'a.id',
  name: 'a.name',
  supplier: 's.name',
  tier: 'st.name', // Sort by tier name, not tier ID
  contract: 'c.contract_number',
  status: 'a.status',
  assessor: 'assessor_name',
  created_at: 'a.created_at'
};

const AssessmentsPage: React.FC = () => {
  const permissions = usePermissions();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State from URL parameters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');

  // Pagination with localStorage persistence
  const { currentPage, pageSize, setCurrentPage, setPageSize, resetToFirstPage } = usePagination({
    storageKey: 'assessments-page-size',
    defaultPageSize: 25
  });

  // Column filters with URL persistence and debounce
  const {
    filterValues: columnFilterValues,
    debouncedFilterValues: debouncedColumnFilters,
    setFilter: setColumnFilter,
    clearAllFilters: clearColumnFilters,
    hasActiveFilters: hasActiveColumnFilters,
    getApiParam: getColumnFiltersParam
  } = useColumnFilters({ columns });

  // Sorting with custom hook
  const { sortConfig, handleSort, isSorted } = useTableSort({
    columnMapping: fieldMapping,
    defaultSort: { field: 'a.created_at', direction: 'DESC' },
    onPageReset: () => setCurrentPage(1)
  });

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    const stored = localStorage.getItem('assessments-visible-columns');
    if (stored) {
      return new Set(JSON.parse(stored));
    }
    return new Set(columns.filter((col) => col.defaultVisible).map((col) => col.key));
  });

  // Save column preferences to localStorage
  useEffect(() => {
    localStorage.setItem('assessments-visible-columns', JSON.stringify(Array.from(visibleColumns)));
  }, [visibleColumns]);

  // Reset to page 1 when filters change
  useEffect(() => {
    resetToFirstPage();
  }, [searchQuery, statusFilter, debouncedColumnFilters, resetToFirstPage]);

  // Debounced search query for conflict filter hook
  const debouncedSearchQuery = useMemo(() => searchQuery, [searchQuery]);

  // Handlers - defined before useScheduleConflictFilter which uses handleFilterChange
  const handleSearch = useCallback(
    (value: string) => {
      setSearchQuery(value);
      const params = new URLSearchParams(searchParams);
      if (value) {
        params.set('search', value);
      } else {
        params.delete('search');
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleFilterChange = useCallback(
    (filter: string) => {
      setStatusFilter(filter);
      const params = new URLSearchParams(searchParams);
      if (filter !== 'all') {
        params.set('status', filter);
      } else {
        params.delete('status');
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Main data fetch using hook
  const {
    assessments,
    isLoading,
    isFetching,
    filterCounts,
    totalItems,
    totalPages
    // refreshData available for future CRUD operations (e.g., delete from list)
  } = useAssessmentPageData({
    queryKey: ['assessments', searchQuery, statusFilter, sortConfig, currentPage, pageSize, debouncedColumnFilters],
    pageSize, // Pass pageSize for correct totalPages calculation
    queryFn: () => {
      const filters = {
        searchTerm: searchQuery || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        columnFilters: getColumnFiltersParam() || undefined,
        sortBy: sortConfig.field,
        order: sortConfig.direction,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize
      };
      return assessmentService.getAssessments(filters);
    }
  });

  // Use hook for conflict filter button management
  const { stateFilters } = useScheduleConflictFilter({
    filterCounts,
    isLoading,
    currentFilter: statusFilter,
    initialFilter: 'all',
    autoSwitchTargetFilter: 'all',
    handleFilterChange,
    debouncedSearchQuery,
    hasActiveColumnFilters,
    baseFilters: baseStatusFilters,
    autoActivateConflicts: true // Auto-switch to conflicts when they exist
  });

  // Toggle column visibility
  const toggleColumn = useCallback((columnKey: string) => {
    const column = columns.find((col) => col.key === columnKey);
    if (column?.alwaysVisible) return;

    setVisibleColumns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  }, []);

  // Get visible columns for rendering
  const getVisibleColumns = useCallback((): TableColumn[] => {
    return columns.filter((col) => visibleColumns.has(col.key));
  }, [visibleColumns]);

  // Export mutation - fetches the full filtered dataset (all pages), not just the current page
  const exportMutation = useMutation({
    mutationFn: () =>
      assessmentService.getAssessmentsForExport({
        searchTerm: searchQuery || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        columnFilters: getColumnFiltersParam() || undefined,
        sortBy: sortConfig.field,
        order: sortConfig.direction
      }),
    onSuccess: (response) => {
      exportAssessments(response.data, Array.from(visibleColumns));
    },
    onError: (err: unknown) => {
      console.error('Failed to export assessments:', err);
      showToast.error(getApiErrorMessage(err, 'Failed to export assessments. Please try again.'));
    }
  });

  const handleExport = useCallback(() => {
    exportMutation.mutate();
  }, [exportMutation]);

  // Cell renderer
  const renderCell = useCallback(
    (column: TableColumn, assessment: Assessment) => {
      switch (column.key) {
        case 'assessment_id':
          return (
            <button className="table-link" onClick={() => navigate(`/assessments/${assessment.assessment_id}`)}>
              {formatAssessmentId(assessment.assessment_id)}
            </button>
          );

        case 'name':
          return <div className={styles.assessmentName}>{assessment.assessment_name}</div>;

        case 'supplier':
          return permissions.canAccessSuppliers ? (
            <button
              className="assessment-link table-link"
              onClick={() => navigate(`/suppliers/${assessment.supplier_id}`)}
            >
              {assessment.supplier_name}
            </button>
          ) : (
            <span>{assessment.supplier_name}</span>
          );

        case 'tier': {
          const hasTierConflict = (assessment.conflicts || []).some(
            (c) => c.status !== 'resolved' && c.conflict_type_code === 'ASSESSMENT_TIER_CHANGE'
          );
          return (
            <div className={styles.tierCellWrapper} data-test-id="tier-cell">
              <TierBadge variant={tierToVariant(assessment.tier_name)}>{assessment.tier_name}</TierBadge>
              {hasTierConflict && (
                <Tooltip content={getTierConflictTooltip(assessment)} position="top" variant="error">
                  <span className={styles.conflictWarningIcon} data-test-id="conflict-warning-icon">
                    <AlertTriangle size={16} />
                  </span>
                </Tooltip>
              )}
            </div>
          );
        }

        case 'contract':
          return permissions.canAccessContracts ? (
            <button
              className="assessment-link table-link"
              onClick={() => navigate(`/contracts/${assessment.contract_id}`)}
            >
              {assessment.contract_number}
            </button>
          ) : (
            <span>{assessment.contract_number}</span>
          );

        case 'contract_name':
          return <div>{assessment.contract_name || '-'}</div>;

        case 'contract_start_date':
          return <div>{formatMediumCalendarDate(assessment.contract_start_date)}</div>;

        case 'contract_end_date':
          return <div>{formatMediumCalendarDate(assessment.contract_end_date)}</div>;

        case 'contract_amount':
          return <div>{formatCurrency(assessment.contract_amount ?? undefined)}</div>;

        case 'contract_type':
          return <div>{assessment.contract_type || '-'}</div>;

        case 'cost_center':
          return <div>{assessment.cost_center || '-'}</div>;

        case 'division':
          return <div>{assessment.division_name || '-'}</div>;

        case 'contract_owner': {
          const owners = assessment.contract_owners;
          return <div>{owners && owners.length > 0 ? owners.map((o) => o.name).join(', ') : '-'}</div>;
        }

        case 'contract_manager': {
          const managers = assessment.contract_managers;
          return <div>{managers && managers.length > 0 ? managers.map((m) => m.name).join(', ') : '-'}</div>;
        }

        case 'assessment_year':
          return <div>{assessment.assessment_year ?? '-'}</div>;

        case 'assessment_meeting_date':
          return <div>{formatMediumCalendarDate(assessment.scheduled_date)}</div>;

        case 'assessment_report_date':
          return <div>{formatMediumCalendarDate(assessment.publish_date)}</div>;

        case 'evidence_due_date':
          return <div>{formatMediumCalendarDate(assessment.evidence_due_date)}</div>;

        case 'date_evidence_verified':
          return <div>{formatMediumCalendarDate(assessment.evidence_received_date)}</div>;

        case 'gaps_identified':
          return <div>{getGapsIdentifiedCount(assessment)}</div>;

        case 'gaps_remediated':
          return <div>{assessment.gaps_remediated_count ?? '-'}</div>;

        case 'gaps_not_remediated':
          return <div>{assessment.gaps_not_remediated_count ?? '-'}</div>;

        case 'status':
          return <Badge variant={statusToVariant(assessment.assessment_status)}>{assessment.assessment_status}</Badge>;

        case 'assessor':
          return (
            <div className={styles.assessorCell}>
              {assessment.assessor_name ? (
                <>
                  <div className={styles.assessorName}>{assessment.assessor_name}</div>
                  {assessment.assessor_email && <div className={styles.assessorEmail}>{assessment.assessor_email}</div>}
                </>
              ) : (
                <span className={styles.unassigned}>Unassigned</span>
              )}
            </div>
          );

        case 'created_at':
          return formatDateOnly(assessment.created_at, '-');

        default:
          return '-';
      }
    },
    [navigate, permissions.canAccessSuppliers, permissions.canAccessContracts]
  );

  return (
    <PageLayout>
      <ListPageHeader
        icon={<ClipboardList size={24} />}
        title="Assessments"
        count={filterCounts.all || 0}
        searchValue={searchQuery}
        onSearchChange={handleSearch}
        searchPlaceholder="Search assessments, suppliers, contracts..."
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExport}
              disabled={exportMutation.isPending || assessments.length === 0}
            >
              <Download size={16} />
              {exportMutation.isPending ? 'Exporting...' : 'Export List'}
            </Button>

            <ColumnSelector
              columns={columns}
              visibleColumns={Array.from(visibleColumns)}
              onToggleColumn={toggleColumn}
            />
          </>
        }
      />

      <FilterButtons
        filters={stateFilters}
        activeFilter={statusFilter}
        onFilterChange={handleFilterChange}
        filterCounts={filterCounts}
        dataTestId="assessments-filter"
      />

      {/* Loading overlay when fetching data */}
      {isFetching && !isLoading && <LoadingOverlay variant="page" />}

      <div className={styles.assessmentsTable}>
        <RZTable
          data={assessments}
          columns={getVisibleColumns()}
          getRowKey={(assessment) => assessment.assessment_id}
          renderCell={renderCell}
          rowClassName={(assessment) => (hasOpenConflicts(assessment) ? 'has-conflict' : '')}
          sorting={{
            sortConfig,
            onSort: handleSort,
            isSorted
          }}
          columnFilters={{
            values: columnFilterValues,
            onChange: setColumnFilter,
            onClearAll: clearColumnFilters,
            hasActiveFilters: hasActiveColumnFilters
          }}
          pagination={{
            currentPage,
            totalPages,
            totalItems,
            pageSize,
            onPageChange: setCurrentPage,
            onPageSizeChange: setPageSize,
            itemName: 'assessments',
            pageSizeOptions: [25, 50, 100]
          }}
          state={{
            isLoading,
            isEmpty: assessments.length === 0,
            emptyTitle: 'No assessments found',
            emptyDescription:
              statusFilter !== 'all'
                ? 'Try changing the filter or search criteria.'
                : 'No assessments available at this time.',
            emptyType: 'default',
            loadingMessage: 'Loading assessments...'
          }}
        />
      </div>
    </PageLayout>
  );
};

export default AssessmentsPage;
